<template>
    <div
        v-if="loading"
        class="flex items-center justify-center min-h-[240px]"
    >
        <ChartStatePlaceholder :loading="loading" />
    </div>

    <div v-else-if="!hasData" class="flex items-center gap-4 sm:gap-6">
        <div class="relative w-40 h-40 flex-shrink-0">
            <div
                class="w-full h-full rounded-full border-[14px] border-slate-100 dark:border-slate-700"
            />
            <div
                class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
            >
                <span class="text-xl font-semibold text-slate-800 dark:text-slate-100">$0</span>
                <span class="text-sm text-slate-500 dark:text-slate-400">Used</span>
            </div>
        </div>
        <div class="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <span class="w-2 h-2 rounded-full bg-slate-300 flex-shrink-0" />
            <span>No data for selected period</span>
        </div>
    </div>

    <div
        v-else
        class="flex flex-col sm:flex-row items-center sm:gap-6 gap-4 w-full min-w-0"
    >
        <div
            class="relative flex-shrink-0"
            :style="{ width: SIZE + 'px', height: SIZE + 'px' }"
        >
            <svg
                :width="SIZE"
                :height="SIZE"
                :viewBox="`0 0 ${SIZE} ${SIZE}`"
                class="block"
            >
                <path
                    v-for="(seg, i) in segments"
                    :key="`seg-${i}`"
                    :d="seg.path"
                    :fill="seg.color"
                    :opacity="hoverIdx === i ? 0.75 : 1"
                    :stroke="themeStore.isDark ? '#1E293B' : '#FFFFFF'"
                    stroke-width="1"
                    stroke-linejoin="round"
                    @mouseenter="onSegHover($event, i)"
                    @mousemove="onSegHover($event, i)"
                    @mouseleave="onSegLeave"
                />
            </svg>
            <div
                class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
            >
                <span
                    class="text-slate-800 dark:text-slate-100 font-inter text-2xl font-medium leading-8"
                >
                    {{ formatMoney(total) }}
                </span>
                <span
                    class="text-slate-500 dark:text-slate-400 font-inter text-sm font-normal leading-5"
                >
                    Used
                </span>
            </div>
        </div>

        <ul class="w-full sm:flex-1 flex flex-col gap-4 min-w-0">
            <li
                v-for="(card, idx) in cards"
                :key="`card-${idx}`"
                class="flex items-start justify-between gap-3"
            >
                <div class="flex items-start gap-2 min-w-0 flex-1">
                    <span
                        class="w-2 h-2 mt-1.5 rounded-full flex-shrink-0"
                        :style="{
                            backgroundColor: PALETTE[idx % PALETTE.length],
                        }"
                    />
                    <div class="min-w-0 flex-1">
                        <div
                            class="flex items-baseline gap-1 min-w-0 text-slate-800 dark:text-slate-100 font-inter text-sm font-normal leading-5"
                        >
                            <span
                                class="block truncate min-w-0"
                                @mouseenter="onNameHover($event, card)"
                                @mouseleave="hideNameTooltip"
                            >
                                {{ card.name }}
                            </span>
                            <span
                                v-if="insightsShowCardNumbers && card.last_4"
                                class="text-slate-500 dark:text-slate-400 font-inter text-sm font-normal leading-4 shrink-0"
                            >
                                ({{ card.last_4 }})
                            </span>
                        </div>
                        <div
                            v-if="card.user_name"
                            class="truncate text-slate-500 dark:text-slate-400 font-inter text-sm font-normal leading-4"
                        >
                            {{ card.user_name }}
                        </div>
                    </div>
                </div>
                <span
                    class="text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5 whitespace-nowrap flex-shrink-0"
                >
                    {{ formatMoney(card.amount) }}
                </span>
            </li>
        </ul>
    </div>

    <!-- Card name tooltip — shown only when the card title is truncated. -->
    <Teleport to="body">
        <div
            v-if="nameTooltip.visible"
            class="fixed z-50 px-2 py-1 rounded-lg shadow-sm bg-[#1E293B] text-white text-xs whitespace-nowrap pointer-events-none"
            :style="{
                left: nameTooltip.x + 'px',
                top: nameTooltip.y + 'px',
                transform: 'translate(-50%, -100%)',
            }"
        >
            {{ nameTooltip.text }}
        </div>
    </Teleport>
</template>

<script setup>
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import {
    formatTooltipMoney,
    isDarkMode,
    tooltipHeader,
} from '@/components/card-benefits/chart-tooltip';
import { useThemeStore } from '@/store/theme';
import ChartStatePlaceholder from './chart-state-placeholder.vue';

const themeStore = useThemeStore();

const props = defineProps({
    cards: {
        type: Array,
        required: true,
    },
});

const store = useCardBenefitsStore();
const { insightsShowCardNumbers, insightsDateRangeLabel, loading } =
    storeToRefs(store);

// Index of the currently hovered donut segment (null when none). Drives
// the FFFFFF 25% overlay highlight (rendered as opacity 0.75 against the
// white card background — equivalent blend).
const hoverIdx = ref(null);

const PALETTE = [
    '#FD421D',
    '#F59E0B',
    '#84CC16',
    '#10B981',
    '#0EA5E9',
    '#3B82F6',
];

const SIZE = 160;
const CX = SIZE / 2;
const CY = SIZE / 2;
const RING_THICKNESS = 16;
// Leave 1px breathing room for the white stroke around each path so it
// doesn't get clipped by the SVG viewBox.
const RO = SIZE / 2 - 1;
const RI = RO - RING_THICKNESS;
const CORNER_RADIUS = 2.5;
const GAP_DEG = 1;

const hasData = computed(
    () =>
        props.cards.length > 0 && props.cards.some((c) => Number(c.amount) > 0)
);

const total = computed(() =>
    props.cards.reduce((sum, c) => sum + Number(c.amount || 0), 0)
);

/**
 * Convert (radius, degrees-from-12-o'clock-clockwise) to SVG (x, y).
 */
function pointAt(radius, deg) {
    const rad = (deg * Math.PI) / 180;
    return [CX + radius * Math.sin(rad), CY - radius * Math.cos(rad)];
}

/**
 * Build the SVG path `d` for a single donut slice with rounded corners.
 *
 * The path traces the outer arc clockwise, fillets at each outer
 * corner (radius `r`), the radial line down to the inner edge,
 * fillets at each inner corner, and the inner arc counter-clockwise.
 * The corner radius is reduced automatically on segments that are too
 * narrow to fit it — preventing the "blob" effect that
 * `stroke-linecap="round"` produces on tiny arcs.
 */
function buildSlicePath(startDeg, endDeg) {
    const sweep = endDeg - startDeg;
    if (sweep <= 0) return '';

    let r = Math.min(CORNER_RADIUS, RING_THICKNESS / 2);
    while (r > 0.1) {
        const aOuter = (Math.asin(r / (RO - r)) * 180) / Math.PI;
        const aInner = (Math.asin(r / (RI + r)) * 180) / Math.PI;
        if (sweep > 2 * Math.max(aOuter, aInner) * 1.05) break;
        r *= 0.7;
    }

    const largeArc = sweep > 180 ? 1 : 0;

    if (r < 0.5) {
        const O1 = pointAt(RO, startDeg);
        const O2 = pointAt(RO, endDeg);
        const I1 = pointAt(RI, startDeg);
        const I2 = pointAt(RI, endDeg);
        return [
            `M ${O1[0]} ${O1[1]}`,
            `A ${RO} ${RO} 0 ${largeArc} 1 ${O2[0]} ${O2[1]}`,
            `L ${I2[0]} ${I2[1]}`,
            `A ${RI} ${RI} 0 ${largeArc} 0 ${I1[0]} ${I1[1]}`,
            'Z',
        ].join(' ');
    }

    const aOuter = (Math.asin(r / (RO - r)) * 180) / Math.PI;
    const aInner = (Math.asin(r / (RI + r)) * 180) / Math.PI;

    const TO1 = pointAt(RO, startDeg + aOuter);
    const TO2 = pointAt(RO, endDeg - aOuter);
    const TI1 = pointAt(RI, startDeg + aInner);
    const TI2 = pointAt(RI, endDeg - aInner);

    const radialOuterDist =
        (RO - r) * Math.cos((aOuter * Math.PI) / 180);
    const radialInnerDist =
        (RI + r) * Math.cos((aInner * Math.PI) / 180);

    const RR_O_start = pointAt(radialOuterDist, startDeg);
    const RR_O_end = pointAt(radialOuterDist, endDeg);
    const RR_I_start = pointAt(radialInnerDist, startDeg);
    const RR_I_end = pointAt(radialInnerDist, endDeg);

    return [
        `M ${RR_O_start[0]} ${RR_O_start[1]}`,
        `A ${r} ${r} 0 0 1 ${TO1[0]} ${TO1[1]}`,
        `A ${RO} ${RO} 0 ${largeArc} 1 ${TO2[0]} ${TO2[1]}`,
        `A ${r} ${r} 0 0 1 ${RR_O_end[0]} ${RR_O_end[1]}`,
        `L ${RR_I_end[0]} ${RR_I_end[1]}`,
        `A ${r} ${r} 0 0 1 ${TI2[0]} ${TI2[1]}`,
        `A ${RI} ${RI} 0 ${largeArc} 0 ${TI1[0]} ${TI1[1]}`,
        `A ${r} ${r} 0 0 1 ${RR_I_start[0]} ${RR_I_start[1]}`,
        'Z',
    ].join(' ');
}

const segments = computed(() => {
    if (!hasData.value) return [];

    if (props.cards.length === 1) {
        // Single segment: render two halves so the SVG arc isn't degenerate.
        return [
            {
                color: PALETTE[0],
                path: [
                    `M ${pointAt(RO, 0)[0]} ${pointAt(RO, 0)[1]}`,
                    `A ${RO} ${RO} 0 1 1 ${pointAt(RO, 359.99)[0]} ${
                        pointAt(RO, 359.99)[1]
                    }`,
                    `L ${pointAt(RI, 359.99)[0]} ${pointAt(RI, 359.99)[1]}`,
                    `A ${RI} ${RI} 0 1 0 ${pointAt(RI, 0)[0]} ${
                        pointAt(RI, 0)[1]
                    }`,
                    'Z',
                ].join(' '),
            },
        ];
    }

    let cumDeg = 0;
    return props.cards.map((card, i) => {
        const portion = Number(card.amount) / total.value;
        const arcDeg = portion * 360;
        const startDeg = cumDeg + GAP_DEG / 2;
        const endDeg = cumDeg + arcDeg - GAP_DEG / 2;
        cumDeg += arcDeg;
        return {
            color: PALETTE[i % PALETTE.length],
            path: buildSlicePath(startDeg, endDeg),
        };
    });
});

const formatMoney = (value) => {
    if (!value && value !== 0) return '$0';
    return '$' + Math.round(Number(value)).toLocaleString('en-US');
};

// Full card identification shown in the legend hover tooltip — supports
// the Figma "Add tooltips with card title for truncated cards" dev note.
const cardTooltipText = (card) => {
    if (insightsShowCardNumbers.value && card.last_4) {
        return `${card.name} (${card.last_4})`;
    }
    return card.name;
};

// Tooltip is only rendered when the card title element actually overflows
// (scrollWidth > clientWidth). Skip otherwise so non-truncated rows like
// "Chase Freedom Flex®" don't trigger a redundant hover bubble.
const nameTooltip = ref({ visible: false, x: 0, y: 0, text: '' });

const onNameHover = (event, card) => {
    const el = event.currentTarget;
    if (!el || el.scrollWidth <= el.clientWidth + 1) return;
    const rect = el.getBoundingClientRect();
    nameTooltip.value = {
        visible: true,
        x: rect.left + rect.width / 2,
        y: rect.top - 8,
        text: cardTooltipText(card),
    };
};

const hideNameTooltip = () => {
    nameTooltip.value.visible = false;
};

const TOOLTIP_ID = 'card-benefits-chart-tooltip';

function ensureTooltipEl() {
    let el = document.getElementById(TOOLTIP_ID);
    if (!el) {
        el = document.createElement('div');
        el.id = TOOLTIP_ID;
        el.style.cssText = [
            'position: fixed',
            'pointer-events: none',
            'opacity: 0',
            'transition: opacity 120ms ease',
            'z-index: 50',
            'font-family: Inter, system-ui, sans-serif',
        ].join(';');
        document.body.appendChild(el);
        const hide = () => {
            el.style.opacity = '0';
        };
        window.addEventListener('scroll', hide, true);
        window.addEventListener('resize', hide);
    }
    return el;
}

function buildCardRow(card, color) {
    const labelText =
        insightsShowCardNumbers.value && card.last_4
            ? `${card.name} <span style="color:#94A3B8;font-weight:400;">(${card.last_4})</span>`
            : card.name;
    const userLine = card.user_name
        ? `<div style="color:${isDarkMode() ? '#94A3B8' : '#64748B'};font-size:12px;font-weight:400;line-height:16px;margin-top:2px;">${card.user_name}</div>`
        : '';
    const dot = color
        ? `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:8px;margin-top:5px;flex-shrink:0;"></span>`
        : `<span style="display:inline-block;width:8px;margin-right:8px;flex-shrink:0;"></span>`;
    return `
        <div style="
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 16px;
            width: 100%;
            font-size: 14px;
            line-height: 20px;
        ">
            <div style="display:flex;align-items:flex-start;min-width:0;flex:1;">
                ${dot}
                <div style="min-width:0;">
                    <div style="color:${isDarkMode() ? '#F1F5F9' : '#1E293B'};font-weight:400;">${labelText}</div>
                    ${userLine}
                </div>
            </div>
            <span style="color:${isDarkMode() ? '#F1F5F9' : '#1E293B'};font-weight:500;white-space:nowrap;">
                ${formatTooltipMoney(card.amount)}
            </span>
        </div>
    `;
}

function buildTotalRow(amount) {
    return `
        <div style="
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            width: 100%;
            font-size: 14px;
            line-height: 20px;
            padding-top: 8px;
            border-top: 1px solid #E2E8F0;
        ">
            <span style="color:${isDarkMode() ? '#F1F5F9' : '#1E293B'};font-weight:400;">Total</span>
            <span style="color:${isDarkMode() ? '#F1F5F9' : '#1E293B'};font-weight:500;white-space:nowrap;">
                ${formatTooltipMoney(amount)}
            </span>
        </div>
    `;
}

function onSegHover(event, idx) {
    const card = props.cards[idx];
    if (!card) return;
    hoverIdx.value = idx;
    const color = PALETTE[idx % PALETTE.length];

    let body;
    if (card.isOther && Array.isArray(card.cards)) {
        // Expanded breakdown for the aggregated "Other N cards" segment:
        // list each underlying card (with owner when multi-user) + Total.
        const rows = card.cards
            .map((c) => buildCardRow(c, color))
            .join('');
        body = `${rows}${buildTotalRow(card.amount)}`;
    } else {
        body = buildCardRow(card, color);
    }

    const html = `
        <div style="
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            padding: 12px 16px;
            border-radius: 8px;
            border: 1px solid ${isDarkMode() ? '#334155' : '#E2E8F0'};
            background: ${isDarkMode() ? '#1E293B' : '#FFFFFF'};
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, ${isDarkMode() ? '0.4' : '0.05'});
            width: 288px;
        ">
            ${tooltipHeader(insightsDateRangeLabel.value)}
            ${body}
        </div>
    `;
    const el = ensureTooltipEl();
    el.innerHTML = html;
    el.style.left = `${event.clientX - el.offsetWidth / 2}px`;
    el.style.top = `${event.clientY - el.offsetHeight - 12}px`;
    el.style.opacity = '1';
}

function onSegLeave() {
    hoverIdx.value = null;
    hideTooltip();
}

function hideTooltip() {
    const el = document.getElementById(TOOLTIP_ID);
    if (el) el.style.opacity = '0';
}
</script>
